import { useAppSelector, RootState } from "../../../../store";
import defaultContent from "../../../../content/downlineProfile";
import "./ContactActions.scss";
import { useTranslation } from "react-i18next";
import { ReactComponent as PhoneSvg } from "../../../../common/icons/IconPhone/svg/phone.svg";
import { ReactComponent as MessageSvg } from "../../../../common/icons/IconMessage/svg/message.svg";
import { IconWhatsAppOutline } from "../../../../common/icons/IconWhatsAppOutline/IconWhatsAppOutline";
import { IconCopy } from "../../../../common/icons/IconCopy/IconCopy";
import { ReactComponent as EmailSvg } from "../../../../common/icons/IconEmail/svg/email.svg";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { PartyInfo } from "../../../../services/Profile/profileAPI.types";
import {
  trackCallAbo,
  trackCallMessage,
  trackCallWhatsapp,
  trackCopyEmail,
  trackCopyPhoneNumber,
  trackEmailAbo,
} from "../../downlineProfileTealium";
import LeafTooltip from "../../../../common/leaf/LeafTooltip/LeafTooltip";
import profileContent from "../../../../content/profile";
import { RefObject, useCallback, useRef } from "react";
import { TooltipRefProps } from "react-tooltip";
import MaskedPhoneNumber from "../../shared/contact/MaskedPhoneNumber";
interface ContactActionsProps {
  partyInfo?: PartyInfo[];
  isLoading?: boolean;
  isConfidential?: boolean;
  isKoreaMarket?: boolean;
}

const ContactActionsSkeleton = () => {
  return (
    <div className="contactActions">
      <div className="contactActions--info">
        <LeafRectangleLoader isLoading height="1.5rem" width="15rem" margin="0" />
      </div>
      <div className="contactActions--info contactActions--info-loader">
        <LeafRectangleLoader isLoading height="1.5rem" width="15rem" margin="0" />
      </div>
    </div>
  );
};

const ContactActions = ({ partyInfo, isLoading, isConfidential, isKoreaMarket = false }: ContactActionsProps) => {
  const { showCallIcon, showMessageIcon, hideDownlineContactDetails } =
    useAppSelector((state: RootState) => state.boot.configuration.profile) ?? {};
  const hideWhatsApp = useAppSelector((state: RootState) => state.boot.configuration.hideWhatsApp);
  const effectiveHideDownlineContactDetails = hideDownlineContactDetails;
  const { t } = useTranslation(["downlineProfile", "profile"]);

  const primaryParty = partyInfo?.find((p) => p.isPrimary) ?? partyInfo?.[0];
  const { displayNumber } = primaryParty?.phoneNumbers?.[0] ?? {};
  const { email } = primaryParty?.emailAddresses?.[0] ?? {};
  const { ecommAddr } = primaryParty?.whatsApp?.[0] ?? {};
  // wa.me links require digits only — strip the leading "+" and any formatting characters.
  const whatsAppContactNumber = ecommAddr?.replace(/\D/g, "");

  const phoneTooltipRef = useRef<TooltipRefProps>(null);
  const emailToolTipRef = useRef<TooltipRefProps>(null);

  const handleTooltipClick = useCallback((ref: RefObject<TooltipRefProps | null>) => {
    if (ref.current) {
      // Force show tooltip on click and hide after 2 seconds
      ref.current.open();
      setTimeout(() => {
        if (ref.current) {
          ref.current.close();
        }
      }, 1000);
    }
  }, []);

  if (isLoading) return <ContactActionsSkeleton />;

  if (isConfidential)
    return (
      <div className="contactActions">
        <div id="contact-actions"></div>
      </div>
    );

  if (!displayNumber && !email)
    return (
      <div className="contactActions">
        <div id="contact-actions"></div>
      </div>
    );

  return (
    <>
      <div className="contactActions">
        <div id="contact-actions"></div>

        <div className="contactActions--info">
          {displayNumber?.trim() && (
            <p className={isKoreaMarket ? "contactActions--korea-phone" : undefined}>
              <MaskedPhoneNumber phone={displayNumber} maskedClassName="contactActions--masked-phone" />
            </p>
          )}
          <div className="contactActions--icons">
            {!isKoreaMarket && showCallIcon && displayNumber?.trim() && (
              <a
                role="button"
                aria-label={t("phoneCall", defaultContent.phoneCall)}
                href={`tel:${displayNumber}`}
                target="_parent"
                className="ContactActions--phone__icon"
                onClick={() => trackCallAbo()}
              >
                <PhoneSvg />
              </a>
            )}
            {!isKoreaMarket && showMessageIcon && displayNumber?.trim() && (
              <a
                onClick={() => trackCallMessage()}
                role="button"
                aria-label={t("sms", defaultContent.sms)}
                href={`sms:${displayNumber}`}
                target="_parent"
              >
                <MessageSvg />
              </a>
            )}
            {!isKoreaMarket && !hideWhatsApp && whatsAppContactNumber?.trim() && (
              <a
                role="button"
                aria-label={t("whatsApp", defaultContent.whatsApp)}
                href={`https://wa.me/${whatsAppContactNumber}`}
                target="_blank"
                rel="noreferrer"
                className="contactActions--phone--whatsapp"
                onClick={() => trackCallWhatsapp()}
              >
                <IconWhatsAppOutline />
              </a>
            )}
            {!isKoreaMarket && showCallIcon && displayNumber?.trim() && (
              <button
                aria-label={t("copyPhone", defaultContent.copyPhone)}
                onClick={() => {
                  navigator.clipboard.writeText(displayNumber ?? "").catch(() => {});
                  trackCopyPhoneNumber();
                  handleTooltipClick(phoneTooltipRef);
                }}
                className="contactActions--icons--copy"
              >
                <LeafTooltip
                  position="left"
                  id="copy-phone"
                  icon={<IconCopy />}
                  content={t("leaf.copyPhoneTooltipText", profileContent.leaf.copyPhoneTooltipText, { ns: "profile" })}
                  openOnClick={true}
                  disableDefaultHandleClick={true}
                  tooltipRef={phoneTooltipRef}
                />
              </button>
            )}
          </div>
        </div>

        {!effectiveHideDownlineContactDetails && email?.trim() && (
          <div className="contactActions--info">
            <p>{email}</p>
            <div className="contactActions--icons">
              <a
                onClick={() => trackEmailAbo()}
                role="button"
                aria-label={t("email", defaultContent.email)}
                href={`mailto:${email}`}
                target="_blank"
                rel="noreferrer"
              >
                <EmailSvg />
              </a>
              <button
                aria-label={t("copyEmail", defaultContent.copyEmail)}
                onClick={() => {
                  navigator.clipboard.writeText(email ?? "").catch(() => {});
                  trackCopyEmail();
                  handleTooltipClick(emailToolTipRef);
                }}
                className="contact-action-tooltip"
              >
                <LeafTooltip
                  position="left"
                  id="copy-email"
                  icon={<IconCopy />}
                  content={t("leaf.copyPhoneTooltipText", profileContent.leaf.copyPhoneTooltipText, { ns: "profile" })}
                  openOnClick={true}
                  disableDefaultHandleClick={true}
                  tooltipRef={emailToolTipRef}
                />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ContactActions;
